import React from 'react';
import { useSelector } from 'react-redux';
import { useParams, useLocation } from 'react-router';
import { useAsyncData } from 'src/hooks/useAsyncData';
import { IAdjustmentReadResourceV22 } from 'components/basket/model/Basket';
import { IOperation, OperationStatus } from 'components/order/model/Operation';
import {
    ExistingSharedOrderData,
    IAdjustmentLocalResource,
    IOrderJoinDetails,
    IOrderReadResourceV18,
    IUpdateOrderAdjustment,
    OrderAdjustmentType,
    OrderErrorCodes
} from 'components/order/model/Order';
import { EOrderNonceKey, orderApi } from 'components/order/orderApi';
import { OPERATION_POLLING_DELAY } from 'config/constants';
import { isDefined, isEmptyString } from 'lib/typeInference';
import { useAuth } from 'lib/useAuth';
import { LocationRouteParams, ROUTES } from 'pages/routes';
import { ApplicationState } from 'store/store';
import { usePayQueryParams } from './usePayDateCheckId';
import { EGAEventName, useGAHelpers } from 'lib/useGAHelpers';
import { useLocalHistory } from 'lib/useLocalHistory';
import { useQuery } from 'src/hooks/useQuery';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useCouponCodes } from 'components/order/coupons/useCouponCodes';
import { getSortedAdjustmentsForCheckout } from 'components/order/helpers';
import { usePlatformAwards } from 'components/order/rewards/usePlatformAwards';
import { useCheckins } from 'components/user/hooks/useCheckins';
import { isLocationOpen } from 'components/location/hooks/useLocationHelpers';
import { resolveTimezone } from 'components/location/openingHoursHelpers';

export interface RecalculateOrderParams {
    newSelectedReward?: IAdjustmentReadResourceV22 | null;
}

enum EGetCheckoutStatus {
    SUCCESS = 'SUCCESS',
    ERROR = 'ERROR',
    INVALID_COUPON = 'INVALID_COUPON',
    INVALID_AWARD = 'INVALID_AWARD'
}

export function usePayAtTable(sharedData: ExistingSharedOrderData | undefined) {
    const { merchantId, locationId } = useParams<LocationRouteParams>();
    const { checkId, tableNumber, date } = usePayQueryParams();
    const orderId = useQuery('orderId');
    const [isLoading, setIsLoading] = React.useState(true);
    const [isOrderNotFound, setIsOrderNotFound] = React.useState(false);
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const { user } = useAuth();
    const { settings } = useSelector((state: ApplicationState) => state.settings);
    const { currentLocation } = useSelector((state: ApplicationState) => state.locations);
    const { logUserEvent } = useGAHelpers();

    const isLocationClosed = React.useMemo(
        () =>
            !isLocationOpen(
                currentLocation?.openingHours,
                resolveTimezone(currentLocation?.timezone, settings?.region.timezone),
                true
            ),
        [currentLocation?.openingHours, currentLocation?.timezone, settings?.region.timezone]
    );
    const { push, replace } = useLocalHistory();
    const location = useLocation();

    const [orderDetails, setOrderDetails] = React.useState<IOrderReadResourceV18 | undefined>();
    const [joinOrderDetails, setJoinOrderDetails] = useAsyncData<IOperation>();
    const [operationError, setOperationError, setOperationErrorInitial] = useAsyncData<IOperation>();
    const [claimedItemIds, setClaimedItemIds] = React.useState<number[]>(sharedData?.claimedItemIds ?? []);

    const {
        value: platformAwards,
        platformAwardsEnabled,
        onAwardAdd,
        onAwardRemove,
        onMultipleAwardsEnable,
        showMoreAwardsButton,
        onAwardsReset,
        awardsForCheckout
    } = usePlatformAwards(sharedData?.selectedRewards ?? [], sharedData?.coupons);
    const { triggerCheckin, checkinOnOrderLocationSelected } = useCheckins();

    const {
        coupons,
        onCouponAdd,
        onCouponRemove,
        onCouponsReset,
        couponAdjustments,
        showCoupons,
        couponsForCheckout
    } = useCouponCodes(sharedData?.coupons, orderDetails?.adjustments);

    const joinOrder = React.useCallback(() => {
        if (isLocationClosed) {
            setIsLoading(false);
            return;
        }
        if ((isDefined(tableNumber) || isDefined(checkId)) && locationId && user) {
            const joiningDetails: IOrderJoinDetails = isDefined(checkId)
                ? {
                      locationId,
                      checkId
                  }
                : {
                      locationId,
                      deliveryLocation: tableNumber,
                      createIfNotFound: settings?.orderToTable?.multiPartEnabled
                  };
            if (isDefined(date)) {
                joiningDetails.checkDate = date;
            }
            if (
                !orderId &&
                ((isDefined(joiningDetails.checkId) && !isEmptyString(joiningDetails.checkId)) ||
                    (isDefined(joiningDetails.deliveryLocation) &&
                        !isEmptyString(joiningDetails.deliveryLocation)))
            ) {
                setOperationErrorInitial();
                setOrderDetails(undefined);
                setIsLoading(true);
                setIsOrderNotFound(false);
                if (checkinOnOrderLocationSelected && locationId) {
                    triggerCheckin(locationId);
                }
                setJoinOrderDetails(() => orderApi.joinOrder(joiningDetails)).then(response => {
                    orderApi.nonceHandlingByOperationStatus(response.status, EOrderNonceKey.JOIN_ORDER);
                    async function getOperationStatus() {
                        if (response.id) {
                            const operation: IOperation = await orderApi.getOperation(
                                response.id?.toString()
                            );
                            switch (operation.status) {
                                case OperationStatus.PENDING:
                                    // Recursive Poll for order completion
                                    setIsLoading(true);
                                    setTimeout(() => {
                                        getOperationStatus();
                                    }, OPERATION_POLLING_DELAY);

                                    orderApi.resetNonceByKey(EOrderNonceKey.JOIN_ORDER);
                                    break;
                                case OperationStatus.ERROR:
                                    if (
                                        operation.errorCode === OrderErrorCodes.CHECK_NOT_FOUND ||
                                        operation.errorCode === OrderErrorCodes.ORDER_NOT_FOUND ||
                                        operation.httpCode === 404
                                    ) {
                                        setIsOrderNotFound(true);
                                        setIsLoading(false);
                                    } else {
                                        setOperationError(() => Promise.resolve(operation));
                                        setIsLoading(false);
                                    }
                                    break;
                                case OperationStatus.TIMEOUT:
                                    throw new Error(operation.errorMessage || 'Operation error or timeout');
                                case OperationStatus.DONE: {
                                    if (!!operation && locationId && merchantId) {
                                        const search = new URLSearchParams(location.search);
                                        search.append('orderId', operation.resultId?.toString() || '');
                                        replace('', { merchantId, locationId }, search.toString());
                                    }
                                }
                            }
                        }
                    }
                    if (response.id) {
                        getOperationStatus();
                    }
                });
            } else {
                setIsLoading(false);
            }
        }
    }, [
        checkId,
        date,
        isLocationClosed,
        location.search,
        locationId,
        merchantId,
        orderId,
        checkinOnOrderLocationSelected,
        triggerCheckin,
        replace,
        setJoinOrderDetails,
        setOperationError,
        setOperationErrorInitial,
        settings?.orderToTable?.multiPartEnabled,
        tableNumber,
        user
    ]);

    const getCheckoutDetails = React.useCallback(
        async (
            adjustments: Partial<IAdjustmentReadResourceV22>[] = [],
            claimedItemIds: number[] = []
        ): Promise<{ result?: IOrderReadResourceV18; status: EGetCheckoutStatus; errorMessage?: string }> => {
            let result: IOrderReadResourceV18 | undefined;
            let errorMessage: string | undefined;
            let status: EGetCheckoutStatus = EGetCheckoutStatus.SUCCESS;
            try {
                result = await orderApi.getCheckoutDetails(
                    orderId,
                    adjustments,
                    undefined,
                    claimedItemIds,
                    []
                );
            } catch (err: any) {
                const couponErrorCodes = [
                    OrderErrorCodes.COUPON_ALREADY_USED,
                    OrderErrorCodes.COUPON_NOT_VALID,
                    OrderErrorCodes.NO_ITEMS_FOR_COUPON,
                    OrderErrorCodes.COUPON_VALIDATION_FAILED,
                    OrderErrorCodes.COUPON_DUPLICATE
                ];
                if (couponErrorCodes.includes(err?.data?.code)) {
                    status = EGetCheckoutStatus.INVALID_COUPON;
                } else {
                    errorMessage = err?.data?.message;
                    status = EGetCheckoutStatus.ERROR;
                }
            }
            return { result, errorMessage, status };
        },
        [orderId]
    );

    React.useEffect(() => {
        joinOrder();
    }, [joinOrder]);

    React.useEffect(() => {
        // Only initial or the order id change
        if (orderId && user && (!orderDetails || orderDetails.id !== Number(orderId))) {
            setIsLoading(true);
            getCheckoutDetails(getSortedAdjustmentsForCheckout(awardsForCheckout, couponsForCheckout))
                .then(({ status, result }) => {
                    switch (status) {
                        case EGetCheckoutStatus.SUCCESS:
                            setOrderDetails(result);
                            break;
                        case EGetCheckoutStatus.INVALID_COUPON:
                            onCouponsReset();
                            break;
                        case EGetCheckoutStatus.ERROR:
                            if (coupons.length) {
                                onCouponsReset();
                                break;
                            }
                            if (platformAwards.length) {
                                onAwardsReset();
                                break;
                            }
                        // eslint-disable-next-line no-fallthrough
                        default: {
                            const search = new URLSearchParams(location.search);
                            if (search.has('orderId')) {
                                search.delete('orderId');
                            }
                            replace('', undefined, search.toString());
                        }
                    }
                })
                .finally(() => setIsLoading(false));
        }
    }, [
        getCheckoutDetails,
        orderDetails,
        orderId,
        replace,
        onCouponsReset,
        user,
        platformAwards.length,
        onAwardsReset,
        coupons.length,
        awardsForCheckout,
        couponsForCheckout,
        location.search
    ]);

    // for user logout
    React.useEffect(() => {
        if (!user) {
            setOrderDetails(undefined);
        }
    }, [user]);

    const handleCheckout = React.useCallback(
        (
            adjustments: Partial<IAdjustmentReadResourceV22>[],
            onSuccess: () => void,
            claimedItemIds?: number[]
        ) =>
            getCheckoutDetails(adjustments, claimedItemIds).then(({ status, result, errorMessage }) => {
                switch (status) {
                    case EGetCheckoutStatus.SUCCESS:
                        onSuccess();
                        setOrderDetails(result);
                        break;
                    case EGetCheckoutStatus.INVALID_COUPON:
                        enqueueSnackbar(t('CHECKOUT_COUPON_INVALID_FORMAT'), { variant: 'error' });
                        break;
                    case EGetCheckoutStatus.ERROR:
                        enqueueSnackbar(errorMessage, { variant: 'error' });
                        break;
                }
            }),
        [enqueueSnackbar, getCheckoutDetails, t]
    );

    const handleCouponAdd = React.useCallback(
        async (couponCode: string) => {
            setIsLoading(true);
            const couponAdjustments: Partial<IAdjustmentLocalResource>[] = [...couponsForCheckout];
            const selectedOn = Date.now();
            couponAdjustments.push({
                type: OrderAdjustmentType.DISCOUNT,
                couponCode,
                selectedOn
            });
            const awardsData = [...awardsForCheckout];
            const localSelectedAward = !platformAwardsEnabled && sharedData?.selectedRewards?.at(-1);
            if (localSelectedAward) {
                awardsData.push({
                    type: OrderAdjustmentType.DISCOUNT,
                    quantity: localSelectedAward.quantity,
                    awardId: localSelectedAward.awardId
                });
            }
            await handleCheckout(
                getSortedAdjustmentsForCheckout(awardsData, couponAdjustments),
                () => {
                    onCouponAdd({ couponCode, selectedOn });
                    if (localSelectedAward) {
                        onAwardAdd(localSelectedAward);
                    }
                    onMultipleAwardsEnable();
                },
                claimedItemIds
            );
            setIsLoading(false);
        },
        [
            couponsForCheckout,
            awardsForCheckout,
            platformAwardsEnabled,
            sharedData?.selectedRewards,
            handleCheckout,
            claimedItemIds,
            onCouponAdd,
            onMultipleAwardsEnable,
            onAwardAdd
        ]
    );

    const handleCouponRemove = React.useCallback(
        async (couponCode: string) => {
            setIsLoading(true);
            const couponAdjustments = couponsForCheckout.filter(adj => adj.couponCode !== couponCode);
            await handleCheckout(
                getSortedAdjustmentsForCheckout(awardsForCheckout, couponAdjustments),
                () => onCouponRemove(couponCode),
                claimedItemIds
            );
            setIsLoading(false);
        },
        [awardsForCheckout, claimedItemIds, couponsForCheckout, handleCheckout, onCouponRemove]
    );

    const handleAwardAdd = React.useCallback(
        async (award: IUpdateOrderAdjustment) => {
            setIsLoading(true);
            const awardAdjustments: Partial<IAdjustmentLocalResource>[] = [...awardsForCheckout];
            const selectedOn = Date.now();
            awardAdjustments.push({
                type: OrderAdjustmentType.DISCOUNT,
                awardId: award.awardId,
                quantity: award.quantity,
                selectedOn
            });
            await handleCheckout(
                getSortedAdjustmentsForCheckout(awardAdjustments, couponsForCheckout),
                () => onAwardAdd(award),
                claimedItemIds
            );
            setIsLoading(false);
        },
        [awardsForCheckout, claimedItemIds, couponsForCheckout, handleCheckout, onAwardAdd]
    );

    const handleAwardRemove = React.useCallback(
        async (awardId: string) => {
            setIsLoading(true);
            const awardAdjustments = awardsForCheckout.filter(adj => adj.awardId !== awardId);
            await handleCheckout(
                getSortedAdjustmentsForCheckout(awardAdjustments, couponsForCheckout),
                () => onAwardRemove(awardId),
                claimedItemIds
            );
            setIsLoading(false);
        },
        [awardsForCheckout, claimedItemIds, couponsForCheckout, handleCheckout, onAwardRemove]
    );

    const handleAwardsClear = React.useCallback(async () => {
        setIsLoading(true);
        await handleCheckout(
            getSortedAdjustmentsForCheckout([], couponsForCheckout),
            () => onAwardsReset(),
            claimedItemIds
        );
        setIsLoading(false);
    }, [claimedItemIds, couponsForCheckout, handleCheckout, onAwardsReset]);

    const handleMultipleAwardsEnable = React.useCallback(
        async (award: IUpdateOrderAdjustment) => {
            logUserEvent(EGAEventName.AwardsAddMoreClick);
            setIsLoading(true);
            const awardAdjustments: Partial<IAdjustmentLocalResource>[] = [...awardsForCheckout];
            awardAdjustments.push({
                type: OrderAdjustmentType.DISCOUNT,
                awardId: award.awardId,
                quantity: award.quantity,
                selectedOn: award.selectedOn
            });
            await handleCheckout(
                getSortedAdjustmentsForCheckout(awardAdjustments, couponsForCheckout),
                () => onMultipleAwardsEnable(award),
                claimedItemIds
            );
            setIsLoading(false);
        },
        [
            awardsForCheckout,
            claimedItemIds,
            couponsForCheckout,
            handleCheckout,
            logUserEvent,
            onMultipleAwardsEnable
        ]
    );

    const multiPartOrderingEnabled = React.useMemo(
        () => settings?.orderToTable?.multiPartEnabled ?? false,
        [settings?.orderToTable?.multiPartEnabled]
    );

    const addMoreItemsSearchString = React.useMemo(() => {
        if (!multiPartOrderingEnabled) {
            return undefined;
        }
        let baseSearch = `orderId=${orderDetails?.id}`;
        if (tableNumber ?? orderDetails?.deliveryLocation) {
            baseSearch += `&tableNumber=${tableNumber ?? orderDetails?.deliveryLocation}`;
        }
        return baseSearch;
    }, [multiPartOrderingEnabled, orderDetails?.deliveryLocation, orderDetails?.id, tableNumber]);

    const onAddMoreItems = React.useCallback(() => {
        logUserEvent(EGAEventName.AddMoreItems);
        push(ROUTES.MENU, {}, addMoreItemsSearchString);
    }, [addMoreItemsSearchString, logUserEvent, push]);

    const onItemSplitSubmit = React.useCallback(
        async (itemIds: number[]) => {
            setIsLoading(true);
            await handleCheckout(
                getSortedAdjustmentsForCheckout(awardsForCheckout, couponsForCheckout),
                () => setClaimedItemIds(itemIds),
                itemIds
            );
            setIsLoading(false);
        },
        [awardsForCheckout, couponsForCheckout, handleCheckout]
    );

    const orderTotals = React.useMemo(() => {
        if (claimedItemIds.length > 0 && orderDetails?.splitTotal) {
            return orderDetails.splitTotal;
        }
        return orderDetails?.total;
    }, [claimedItemIds.length, orderDetails?.splitTotal, orderDetails?.total]);

    const isEmpty = React.useMemo(
        () =>
            !isLoading &&
            !orderDetails?.items?.length &&
            !orderDetails?.adjustments?.length &&
            !orderDetails?.payments?.length,
        [
            isLoading,
            orderDetails?.adjustments?.length,
            orderDetails?.items?.length,
            orderDetails?.payments?.length
        ]
    );

    return {
        coupons: {
            onCouponAdd: handleCouponAdd,
            onCouponRemove: handleCouponRemove,
            value: coupons,
            couponAdjustments,
            showCoupons
        },
        awards: {
            value: platformAwards,
            platformAwardsEnabled,
            onClear: handleAwardsClear,
            onAwardAdd: handleAwardAdd,
            onAwardRemove: handleAwardRemove,
            onMultipleAwardsEnable: handleMultipleAwardsEnable,
            showMoreAwardsButton
        },
        isEmpty,
        isLoading: isLoading || !user,
        orderTotals,
        orderDetails,
        claimedItemIds,
        operationError,
        isOrderNotFound,
        isLocationClosed,
        joinOrderDetails,
        multiPartOrderingEnabled,
        onAddMoreItems,
        onItemSplitSubmit,
        setOperationErrorInitial
    };
}
